import { Card, FormHelperText, Input, Option, Select, Textarea } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import isEmail from 'is-email';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetAddressContactsQueryOptions,
  getGetAddressesQueryOptions,
} from '../../../../api/generated/address-controller/address-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  LocaleDto,
  PatientAddressDtoReferenceType,
  PatientDto,
  PatientDtoGender,
  PatientDtoResidencyPermit,
} from '../../../../api/generated/model';
import { ReferencePersonDto } from '../../../../api/generated/model/bezugspersonDto.ts';
import {
  getGetPatientByIdQueryKey,
  useUpdatePatient,
} from '../../../../api/generated/patient-controller/patient-controller.ts';
import { PersonLivingStatus } from '../../../../api/generated/person-controller/person-controller.ts';
import { GermanDateInput } from '../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../components/form/InstitutionContactFields.tsx';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../../components/form/SingeLineFormComponents.tsx';
import { LeavePageWarning } from '../../../../components/LeavePageWarning.tsx';
import { PostalCodeFields } from '../../../../components/PostalCodeFields.tsx';
import { getCurrentDate } from '../../../../utils/date.ts';
import { getGenderName } from '../../../../utils/gender.ts';
import { getLivingStatusName } from '../../../../utils/livingStatus.ts';
import { isPhoneNumberValid } from '../../../../utils/phoneNumber.ts';
import { getResidencyPermitName } from '../../../../utils/residencyPermit.ts';
import { getStringOrUndefined } from '../../../../utils/string.ts';
import { SelectReferencePersonMulti } from '../../../components/bezugsperson/SelectReferencePersonMulti.tsx';
import { SelectCountry } from '../../../components/SelectCountry.tsx';
import { SelectLanguage } from '../../../components/SelectLanguage.tsx';
import { EditPatientFormHeader } from './EditPatientFormHeader.tsx';

export function EditPatientForm({ patient, onPersonSynced }: { patient: PatientDto; onPersonSynced?: () => void }) {
  const queryClient = useQueryClient();

  const updatePatientMutation = useUpdatePatient({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetPatientByIdQueryKey(patient.id), data);
        void queryClient.resetQueries(getGetAddressesQueryOptions({ patientId: patient.id }));
        void queryClient.resetQueries(
          getGetAddressContactsQueryOptions(addressValue?.id ?? '', { patientId: patient.id }),
        );
      },
      onError: () =>
        toast.error('Bei dem Speichern des Patienten ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });

  const {
    register,
    control,
    getFieldState,
    watch,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors, isDirty: isFormDirty, isValid, dirtyFields: _dirtyFields },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(patient),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const savePatient = useCallback(
    async (data: FormValues) => {
      toast.dismiss('validationError');
      const selectedBezugspersonen = (data.bezugspersonen ?? []).filter((person) => person.isSelected);
      const bezugspersonIds = selectedBezugspersonen
        .map((person) => person.id)
        .filter((id): id is string => Boolean(id));
      const payload: PatientDto & { bezugspersonIds: string[] } = {
        id: patient.id,
        salutation: data.salutation || undefined,
        firstName: data.firstName,
        lastName: data.lastName,
        birthDate: data.birthDate,
        gender: data.gender,
        livingStatus: data.livingStatus ?? null,
        note: getStringOrUndefined(data.note),
        phone: getStringOrUndefined(data.phone),
        email: getStringOrUndefined(data.email),

        mobilePhoneNumber: getStringOrUndefined(data.mobilePhoneNumber),
        privatePhoneNumber: getStringOrUndefined(data.privatePhoneNumber),
        workPhoneNumber: getStringOrUndefined(data.workPhoneNumber),

        nationality: data.nationality ?? undefined,
        origin: data.origin ?? undefined,
        arrival: getStringOrUndefined(data.arrival),
        asylumNumber: getStringOrUndefined(data.asylumNumber),
        zemisNumber: getStringOrUndefined(data.zemisNumber),

        caseNumber:
          queryClient.getQueryData<PatientDto>(getGetPatientByIdQueryKey(patient.id))?.caseNumber ??
          patient.caseNumber,
        languages: data.languages ?? [],
        residencyPermit: data.residencyPermit,

        postalCode: data.postalCode ?? undefined,
        city: getStringOrUndefined(data.city),
        state: getStringOrUndefined(data.state),
        entryDate: getStringOrUndefined(data.entryDate),
        additionalInformation: getStringOrUndefined(data.additionalInformation),
        street: getStringOrUndefined(data.street),
        bezugspersonen: selectedBezugspersonen,
        bezugspersonIds,

        address: data.address
          ? {
              address: data.address,
              contact: data.contact ?? undefined,
              from: getCurrentDate(),
              patientId: patient.id,
              referenceType: PatientAddressDtoReferenceType.PATIENT_RESIDENCE,
            }
          : undefined,
      };
      const savedPatient = await updatePatientMutation.mutateAsync({ data: payload });
      const nextPatient = withSelectedBezugspersonen(savedPatient, selectedBezugspersonen);
      queryClient.setQueryData(getGetPatientByIdQueryKey(patient.id), nextPatient);
      reset(getDefaultValues(nextPatient));
    },
    [patient, queryClient, reset, updatePatientMutation],
  );

  const handleFormSubmit = useCallback(
    async (data: FormValues) => {
      updatePatientMutation.reset();
      await savePatient(data);
    },
    [savePatient, updatePatientMutation],
  );

  const handleSave = useCallback(() => {
    void handleSubmit(handleFormSubmit, () =>
      toast.warn('Patient kann aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    )();
  }, [handleFormSubmit, handleSubmit]);

  const handleCancel = useCallback(() => {
    reset(getDefaultValues(patient));
    toast.dismiss('validationError');
  }, [patient, reset]);

  const isSaving = updatePatientMutation.isPending;
  const isSaveDisabled = !isFormDirty || !isValid || isSaving;

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updatePatientMutation.isPending,
    withResolver: true,
  });

  const addressValue = watch('address');

  return (
    <>
      <Card>
        <SingeLineFormGrid>
          <EditPatientFormHeader
            patient={patient}
            onPersonSynced={onPersonSynced}
            onSave={handleSave}
            onCancel={handleCancel}
            isDirty={isFormDirty}
            isSaving={isSaving}
            isSaveDisabled={isSaveDisabled}
          />
          <SingleLineFormControl<FormValues> label="Anrede" fieldState={getFieldState('salutation')}>
            <Controller
              name="salutation"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  onChange={(_, value) => onChange(value ?? '')}
                  value={value || null}
                  variant="plain"
                  placeholder="Bitte auswählen">
                  <Option value="MR">Herr</Option>
                  <Option value="MRS">Frau</Option>
                </Select>
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl label="Nachname" fieldState={getFieldState('lastName')}>
            <Input {...register('lastName', { required: true })} variant="plain" />
            {errors.lastName && <FormHelperText>Nachname muss ausgefüllt werden</FormHelperText>}
          </SingleLineFormControl>
          <SingleLineFormControl label="Vorname" fieldState={getFieldState('firstName')}>
            <Input {...register('firstName', { required: true })} variant="plain" />
            {errors.firstName && <FormHelperText>Vorname muss ausgefüllt werden</FormHelperText>}
          </SingleLineFormControl>
          <SingleLineFormControl label="Geburtsdatum" fieldState={getFieldState('birthDate')}>
            <GermanDateInput
              dateInputMode="formatted"
              control={control}
              name="birthDate"
              rules={{
                required: true,
                max: { value: getCurrentDate(), message: 'range' },
              }}
              max={getCurrentDate()}
              variant="plain"
            />
            {errors.birthDate && (
              <FormHelperText>
                {errors.birthDate.type === 'required'
                  ? 'Geburtsdatum muss ausgefüllt werden'
                  : 'Geburtsdatum darf nicht in der Zukunft liegen'}
              </FormHelperText>
            )}
          </SingleLineFormControl>
          <SingleLineFormControl label="Geschlecht" fieldState={getFieldState('gender')}>
            <Controller
              name={'gender'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select onChange={(_, value) => onChange(value)} value={value} variant="plain">
                  {Object.values(PatientDtoGender).map((gender) => (
                    <Option value={gender} key={gender}>
                      {getGenderName(gender)}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl<FormValues> label="Telefon Nr. Mobil" fieldState={getFieldState('mobilePhoneNumber')}>
            <Input
              {...register('mobilePhoneNumber', {
                validate: (mobilePhoneNumber) => !mobilePhoneNumber || isPhoneNumberValid(mobilePhoneNumber),
              })}
              type="tel"
              variant="plain"
            />
            {errors.mobilePhoneNumber && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
          </SingleLineFormControl>

          {/* ADDED: private phone field copied in the same style as EditPersonForm */}
          <SingleLineFormControl<FormValues>
            label="Telefon Nr. Privat"
            fieldState={getFieldState('privatePhoneNumber')}>
            <Input
              {...register('privatePhoneNumber', {
                validate: (privatePhoneNumber) => !privatePhoneNumber || isPhoneNumberValid(privatePhoneNumber),
              })}
              type="tel"
              variant="plain"
            />
            {errors.privatePhoneNumber && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
          </SingleLineFormControl>

          {/* ADDED: work phone field copied in the same style as EditPersonForm */}
          <SingleLineFormControl<FormValues> label="Telefon Nr. Geschäft" fieldState={getFieldState('workPhoneNumber')}>
            <Input
              {...register('workPhoneNumber', {
                validate: (workPhoneNumber) => !workPhoneNumber || isPhoneNumberValid(workPhoneNumber),
              })}
              type="tel"
              variant="plain"
            />
            {errors.workPhoneNumber && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
          </SingleLineFormControl>
          <SingleLineFormControl label="E-Mail-Adresse" fieldState={getFieldState('email')}>
            <Input
              {...register('email', { validate: (email) => !email || isEmail(email) })}
              type="email"
              variant="plain"
            />
            {errors.email && <FormHelperText>Diese E-Mail-Adresse ist nicht korrekt</FormHelperText>}
          </SingleLineFormControl>
          <SingleLineFormControl label="Nationalität" fieldState={getFieldState('nationality')}>
            <Controller
              name={'nationality'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <SelectCountry onChange={onChange} value={value} variant={'plain'} />
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl label="Herkunftsland" fieldState={getFieldState('origin')}>
            <Controller
              name={'origin'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <SelectCountry onChange={onChange} value={value} variant={'plain'} />
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl label="Einreisedatum" fieldState={getFieldState('arrival')}>
            <Input {...register('arrival')} variant="plain" />
          </SingleLineFormControl>
          <SingleLineFormControl label="FREPO-Status" fieldState={getFieldState('residencyPermit')}>
            <Controller
              name={'residencyPermit'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select onChange={(_, value) => onChange(value)} value={value} variant="plain">
                  {Object.values(PatientDtoResidencyPermit).map((permit) => (
                    <Option value={permit} key={permit}>
                      {getResidencyPermitName(permit)}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl label="N-Nummer" fieldState={getFieldState('asylumNumber')}>
            <Input {...register('asylumNumber')} variant="plain" />
          </SingleLineFormControl>
          <SingleLineFormControl<FormValues> label="ZEMIS-Nummer" fieldState={getFieldState('zemisNumber')}>
            <Input {...register('zemisNumber')} variant="plain" />
          </SingleLineFormControl>
          <SingleLineFormControl label="Sprachen" fieldState={getFieldState('languages')}>
            <Controller
              name="languages"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SelectLanguage
                  value={value ?? []}
                  onChange={(newValue) => {
                    onChange(newValue ?? []);
                  }}
                  variant="plain"
                />
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl<FormValues> label="Zusatz" fieldState={getFieldState('additionalInformation')}>
            <Input
              {...register('additionalInformation')}
              variant="plain"
              // placeholder="Zusatz"
            />
          </SingleLineFormControl>

          <SingleLineFormControl<FormValues> label="Strasse" fieldState={getFieldState('street')}>
            <Input
              {...register('street')}
              variant="plain"
              // placeholder="Strasse"
            />
          </SingleLineFormControl>
          <PostalCodeFields<FormValues>
            control={control}
            setValue={setValue}
            getValues={getValues}
            zipName="postalCode"
            cityName="city"
            cantonName="state"
            zipLabel="PLZ"
            cityLabel="Ort"
            cantonLabel="Kanton"
            layout="singleLine"
            getFieldState={getFieldState}
            // enabled={open}
            variant="plain"
          />
          <InstitutionContactFields
            formMethods={{ control, watch, setValue, getFieldState }}
            layout="singleLine"
            addressField="address"
            contactField="contact"
            addressLabel="Institution"
            contactLabel="Ansprechperson"
            addressTypes={[AddressDtoAddressType.ACCOMMODATION_PRIVATE, AddressDtoAddressType.ACCOMMODATION_ASYLUM]}
            patientId={patient.id}
            canChangeAddressType={true}
            variant="plain"
            showSyncIcon={true}
            clearContactOnAddressChange="always"
            contactClearOptions={false}
            useAllContactsList={true}
          />

          <SingleLineFormControl<FormValues> label="Bezugsperson" fieldState={getFieldState('bezugspersonen')}>
            <Controller
              name="bezugspersonen"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SelectReferencePersonMulti
                  mode="edit"
                  // disabled={true}
                  personId={patient.id}
                  createEndpoint="patient"
                  fetchedOptions={patient.bezugspersonen ?? []}
                  value={value ?? []}
                  onChange={onChange}
                  variant="plain"
                />
              )}
            />
          </SingleLineFormControl>

          <SingleLineFormControl<FormValues> label="Lebensstatus" fieldState={getFieldState('livingStatus')}>
            <Controller
              name="livingStatus"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  onChange={(_, value) => onChange(value)}
                  value={value}
                  variant="plain"
                  placeholder="Bitte auswählen">
                  {Object.values(PersonLivingStatus).map((status) => (
                    <Option value={status} key={status}>
                      {getLivingStatusName(status)}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl<FormValues> label="Bemerkungen" fieldState={getFieldState('note')}>
            <Textarea {...register('note')} minRows={3} maxRows={6} variant="plain" />
          </SingleLineFormControl>
        </SingeLineFormGrid>
      </Card>
      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onDelete={() => blocker.proceed?.()}
        onSave={() => {
          if (blocker.reset) {
            blocker.reset();
          }
          handleSave();
        }}
        title={`Daten von ${patient.firstName} ${patient.lastName} behalten?`}
      />
    </>
  );
}

interface FormValues {
  salutation: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: PatientDtoGender;
  livingStatus: PersonLivingStatus | null;
  note: string;

  // Phones
  phone?: string;
  mobilePhoneNumber: string;
  privatePhoneNumber: string;
  workPhoneNumber: string;

  // Contact
  email: string;

  // Nationality / origin
  nationality: LocaleDto | null;
  origin: LocaleDto | null;

  // Dates & identifiers
  arrival: string;
  entryDate: string;
  asylumNumber: string;
  zemisNumber: string;

  // Languages & permit
  languages: LocaleDto[];
  residencyPermit: PatientDtoResidencyPermit;

  // Address-related (object-based, not string)
  address: AddressDto | null;
  contact: AddressContactDto | null;

  // Location fields (if backend expects them separately)
  postalCode: string;
  city: string;
  state: string;

  bezugspersonen: ReferencePersonDto[];

  // Extra
  additionalInformation: string;
  street: string;
}

function withSelectedBezugspersonen(
  patient: PatientDto,
  selectedBezugspersonen: ReferencePersonDto[],
): PatientDto {
  const selectedIds = new Set(
    selectedBezugspersonen.map((person) => person.id).filter((id): id is string => Boolean(id)),
  );
  const source = patient.bezugspersonen?.length ? patient.bezugspersonen : selectedBezugspersonen;

  return {
    ...patient,
    bezugspersonen: source.map((person) => ({
      ...person,
      isSelected: Boolean(person.id && selectedIds.has(person.id)),
    })),
  };
}

function getDefaultValues(patient: PatientDto): FormValues {
  return {
    salutation: patient.salutation ?? '',
    firstName: patient.firstName,
    lastName: patient.lastName,
    birthDate: patient.birthDate,
    gender: patient.gender,
    livingStatus: patient.livingStatus ?? null,
    note: patient.note ?? '',
    phone: patient.phone ?? '',
    email: patient.email ?? '',
    nationality: patient.nationality ?? null,
    origin: patient.origin ?? null,
    arrival: patient.arrival ?? '',
    asylumNumber: patient.asylumNumber ?? '',
    languages: patient.languages ?? [],
    residencyPermit: patient.residencyPermit,
    address: patient.address?.address ?? null,
    contact: patient.address?.contact ?? null,
    mobilePhoneNumber: patient.mobilePhoneNumber ?? '',
    privatePhoneNumber: patient.privatePhoneNumber ?? '',
    workPhoneNumber: patient.workPhoneNumber ?? '',
    entryDate: patient.entryDate ?? '',
    zemisNumber: patient.zemisNumber ?? '',
    postalCode: patient.postalCode != null ? String(patient.postalCode) : '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    additionalInformation: patient.additionalInformation ?? '',
    street: patient.street ?? '',
    bezugspersonen: patient.bezugspersonen ?? [],
  };
}
